import React, { useEffect, useState } from "react";
import Dropdown from "../../static/Dropdown/Dropdown";
import { dropdownItem } from "../../static/Dropdown/types";
import { Federation } from "./type";
import "./index.scss";
import {
  getNationalAssociations,
  getRegionalAssociations,
  getAssociationData,
  getAllAssociationsData
} from "../../services/ma";
import Loading from "../../static/Loading/Loading";
import { associations as errors } from "../../helpers/errors/index";
import { useLazyLoad } from "../../helpers/hooks";

const MembersAssociation = () => {
  const [naOptions, setNaOptions] = useState<dropdownItem[]>([]);
  const [raOptions, setRaOptions] = useState<dropdownItem[]>([]);

  const [isLoadingNaOpt, setIsLoadingNaOpt] = useState(false);
  const [isLoadingRaOpt, setIsLoadingRaOpt] = useState(false);
  const [isLoadingAssociation, setIsLoadingAssociation] = useState(false);

  const [isErrorNaOpt, setIsErrorNaOpt] = useState(false);
  const [isErrorRaOpt, setIsErrorRaOpt] = useState(false);
  const [isErrorAssociation, setIsErrorAssociation] = useState(false);

  const [selectedNa, setSelectedNa] = useState<dropdownItem | null>();
  const [selectedRa, setSelectedRa] = useState<dropdownItem | null>();

  const [associations, setAssociation] = useState<Federation[]>([]);
  const [associationType, setAssociationType] = useState<"NA" | "RA">();

  useEffect(() => {
    setIsLoadingNaOpt(true);
    getNationalAssociations()
      .then(data => {
        const options = data.map((dataItem: any) => {
          return {
            label: dataItem.nationName,
            value: dataItem.bid
          };
        });
        setNaOptions(options);
        setIsLoadingNaOpt(false);
      })
      .catch(() => {
        setIsLoadingNaOpt(false);
        setIsErrorNaOpt(true);
      });

    setIsLoadingRaOpt(true);
    getRegionalAssociations()
      .then(data => {
        const options = data.map((dataItem: any) => {
          return {
            label: dataItem.regionAssociationName,
            value: dataItem.bid
          };
        });
        setRaOptions(options);
        setIsLoadingRaOpt(false);
      })
      .catch(() => {
        setIsLoadingRaOpt(false);
        setIsErrorRaOpt(true);
      });

    setIsLoadingAssociation(true);
    setIsErrorAssociation(false);
    getAllAssociationsData()
      .then(data => {
        setAssociation(data);
        setIsLoadingAssociation(false);
      })
      .catch(() => {
        setIsLoadingAssociation(false);
        setIsErrorAssociation(true);
      });
  }, []);

  const updateAssociation = (option: dropdownItem, type: "NA" | "RA") => {
    setIsLoadingAssociation(true);
    setIsErrorAssociation(false);
    getAssociationData(option.value as string)
      .then(data => {
        setAssociation([data]);
        setAssociationType(type);
        if (type === "NA") {
          setSelectedNa(option);
          setSelectedRa(null);
        } else if (type === "RA") {
          setSelectedRa(option);
          setSelectedNa(null);
        }
        setIsLoadingAssociation(false);
      })
      .catch(() => {
        setIsLoadingAssociation(false);
        setIsErrorAssociation(true);
      });
  };

  return (
    <>
      <div className="membersa__dropdowns">
        {!isLoadingNaOpt && !isLoadingRaOpt ? (
          isErrorNaOpt || isErrorRaOpt ? (
            <p className="error-text">{errors.generalError}</p>
          ) : (
            <>
              <Dropdown
                className="dropdown--na"
                placeholder="Select a national association"
                onChange={option => {
                  updateAssociation(option, "NA");
                }}
                options={naOptions}
                value={selectedNa}
              />
              <span className="membersa__or">or</span>
              <Dropdown
                className="dropdown--a"
                placeholder="Select a regional association"
                onChange={option => {
                  updateAssociation(option, "RA");
                }}
                options={raOptions}
                value={selectedRa}
              />
            </>
          )
        ) : (
          <Loading />
        )}
      </div>
      {associations && !isLoadingAssociation ? (
        isErrorAssociation ? (
          <p className="error-text">{errors.specificError}</p>
        ) : (
          <>
            {associationType === "RA" ? <h2>Regional Association</h2> : null}
            {associationType === "NA" ? <h2>National Association</h2> : null}
            {associations.map((association, index) => {
              const president =
                association &&
                association.AssociationContact.filter(contact => {
                  return contact.role === "President";
                })[0];

              const secretary =
                association &&
                association.AssociationContact.filter(contact => {
                  return contact.role === "Secretary";
                })[0];

              const isMailToLink =
                association &&
                association.email &&
                association.email.split("@").length === 2;

              return (
                <div className="membersa__assocations" key={index}>
                  <div className="membersa__assocation-details">
                    <div className="membersa__focused-details">
                      <p className="membersa__name">
                        {association.orgCode && associationType === "NA" ? (
                          <span
                            className={`itf-flags itf-flags--${association.orgCode}`}
                          />
                        ) : null}
                        {association.memberAssociation}
                      </p>
                      <div className="membersa__address">
                        {association.addressLine1}
                      </div>
                      {association.addressLine2 ? (
                        <div className="membersa__address">
                          {association.addressLine2}
                        </div>
                      ) : null}
                      {association.addressLine3 ? (
                        <div className="membersa__address">
                          {association.addressLine3}
                        </div>
                      ) : null}
                      {association.addressLine4 ? (
                        <div className="membersa__address">
                          {association.addressLine4}
                        </div>
                      ) : null}
                      {association.addressLine5 ? (
                        <div className="membersa__address">
                          {association.addressLine5}
                        </div>
                      ) : null}
                      {association.postcode ? (
                        <div className="membersa__address">
                          {association.postcode}
                        </div>
                      ) : null}
                    </div>
                    <div className="membersa__secondary-details">
                      <ul className="membersa-details-list">
                        <li className="membersa-details-list__item">
                          <span className="membersa-details-list__label">
                            Telephone:
                          </span>
                          <span className="membersa-details-list__detail">
                            {association.telephone}
                          </span>
                        </li>
                        <li className="membersa-details-list__item">
                          <span className="membersa-details-list__label">
                            Fax:
                          </span>
                          <span className="membersa-details-list__detail">
                            {association.fax}
                          </span>
                        </li>
                        <li className="membersa-details-list__item">
                          <span className="membersa-details-list__label">
                            President:
                          </span>
                          <span className="membersa-details-list__detail">
                            {president &&
                              `${president.firstName} ${president.lastName}`}
                          </span>
                        </li>
                        <li className="membersa-details-list__item">
                          <span className="membersa-details-list__label">
                            Secretary:
                          </span>
                          <span className="membersa-details-list__detail">
                            {secretary &&
                              `${secretary.firstName} ${secretary.lastName}`}
                          </span>
                        </li>
                        <li className="membersa-details-list__item">
                          <span className="membersa-details-list__label">
                            Email:
                          </span>
                          {isMailToLink ? (
                            <span className="membersa-details-list__detail">
                              <a href={`mailto:${association.email}`}>
                                {association.email}
                              </a>
                            </span>
                          ) : (
                            <span className="membersa-details-list__detail">
                              {association.email}
                            </span>
                          )}
                        </li>
                        <li className="membersa-details-list__item">
                          <span className="membersa-details-list__label">
                            Website:
                          </span>
                          <span className="membersa-details-list__detail">
                            {association.website ? (
                              <a target="_blank" href={association.website}>
                                {association.website}
                              </a>
                            ) : null}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )
      ) : isLoadingAssociation ? null : (
        <p>No Association selected</p>
      )}
      {isLoadingAssociation ? (
        <div className="ma-loader">
          <Loading />
        </div>
      ) : null}
    </>
  );
};

export default () => useLazyLoad(<MembersAssociation />);
